import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  CloudRain,
  Contact,
  FileCheck2,
  Files,
  FolderKanban,
  GanttChart,
  HardHat,
  HelpCircle,
  LayoutDashboard,
  Link2,
  ListChecks,
  MessageCircleQuestion,
  Plug,
  Receipt,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  UserCog,
  Users,
  Wrench,
} from "lucide-react";

export type PbosRole = "admin" | "project_manager" | "contractor" | "supervisor";

export interface NavChild {
  label: string;
  icon: LucideIcon;
  path: string;
}

export interface NavItem {
  label: string;
  icon: LucideIcon;
  path?: string;
  children?: NavChild[];
  separatorBefore?: boolean;
}

const allItems: NavItem[] = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/" },
  { label: "Timeline", icon: GanttChart, path: "/timeline" },
  { label: "Projects", icon: FolderKanban, path: "/projects" },
  {
    label: "Documents",
    icon: Files,
    children: [
      { label: "All Documents", icon: Files, path: "/documents" },
      { label: "Document Shares", icon: Link2, path: "/document-shares" },
    ],
  },
  {
    label: "Field Ops",
    icon: ClipboardCheck,
    children: [
      { label: "Daily Logs", icon: CloudRain, path: "/daily-logs" },
      { label: "Inspections", icon: ClipboardCheck, path: "/inspections" },
      { label: "Punch List", icon: ListChecks, path: "/punch-list" },
      { label: "Safety Incidents", icon: Shield, path: "/safety-incidents" },
    ],
  },
  {
    label: "Directory",
    icon: Users,
    separatorBefore: true,
    children: [
      { label: "Company", icon: HardHat, path: "/companies" },
      { label: "Contacts", icon: Contact, path: "/contacts" },
    ],
  },
  {
    label: "Finance",
    icon: Receipt,
    children: [
      { label: "Commitments", icon: ScrollText, path: "/my-commitments" },
      { label: "Compliance", icon: ShieldCheck, path: "/compliance" },
      {
        label: "Subcontract Pay Applications",
        icon: Receipt,
        path: "/pay-applications/subcontract",
      },
      { label: "Owner Pay Applications", icon: Receipt, path: "/pay-applications/owner" },
      { label: "General Requirements", icon: ClipboardPen, path: "/general-requirements" },
    ],
  },
  {
    label: "System",
    icon: Settings,
    children: [
      { label: "Configuration", icon: Wrench, path: "/admin/config" },
      { label: "Integrations", icon: Plug, path: "/admin/integrations" },
      { label: "Admin", icon: Shield, path: "/admin" },
      { label: "User", icon: UserCog, path: "/admin/users" },
      { label: "Audit Trail", icon: ClipboardList, path: "/admin/audit" },
    ],
  },
  {
    label: "Help",
    icon: HelpCircle,
    children: [{ label: "Glossary", icon: BookOpen, path: "/glossary" }],
  },
];

const contractorAllowedPaths = new Set<string>([
  "/",
  "/projects",
  "/my-commitments",
  "/my-pay-apps",
  "/rfis",
  "/submittals",
  "/punch-list",
  "/compliance",
]);

const supervisorAllowedPaths = new Set<string>([
  "/daily-logs",
  "/inspections",
  "/punch-list",
  "/safety-incidents",
]);

// Contractor-only nav entries that don't appear in `allItems` (admin/PM views
// don't surface them). Appended to the contractor branch in `getNavForRole`.
const contractorExtraItems: NavItem[] = [
  { label: "Pay Applications", icon: Receipt, path: "/my-pay-apps" },
  { label: "RFIs", icon: MessageCircleQuestion, path: "/rfis" },
  { label: "Submittals", icon: FileCheck2, path: "/submittals" },
];

export function getNavForRole(role: PbosRole): NavItem[] {
  if (role === "admin") return allItems;

  if (role === "project_manager") {
    return allItems.filter((item) => item.label !== "System");
  }

  if (role === "supervisor") {
    // supervisor: keep parent groups intact; only show Field Ops children
    const out: NavItem[] = [];
    for (const item of allItems) {
      if (!item.children) continue;
      const allowed = item.children.filter((c) => supervisorAllowedPaths.has(c.path));
      if (allowed.length > 0) {
        out.push({ label: item.label, icon: item.icon, children: allowed });
      }
    }
    return out;
  }

  // contractor: flatten; keep only whitelisted paths; drop dropdown wrappers
  const out: NavItem[] = [];
  for (const item of allItems) {
    if (item.path && contractorAllowedPaths.has(item.path)) {
      out.push(item);
      continue;
    }
    if (item.children) {
      const allowedChildren = item.children.filter((c) => contractorAllowedPaths.has(c.path));
      if (allowedChildren.length === 1) {
        const [only] = allowedChildren;
        if (only) out.push({ label: only.label, icon: only.icon, path: only.path });
      } else if (allowedChildren.length > 1) {
        out.push({ label: item.label, icon: item.icon, children: allowedChildren });
      }
    }
  }
  out.push(...contractorExtraItems);
  return out;
}

export function resolvePbosRole(rawRole: string | undefined | null): PbosRole {
  if (rawRole === "admin") return "admin";
  if (rawRole === "contractor") return "contractor";
  if (rawRole === "supervisor") return "supervisor";
  return "project_manager";
}
